import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { ApiError, newIdempotencyKey } from '../../lib/api';
import { stripLogoUrl } from '../style/binaryApi';
import { LOCAL_PRESETS } from '../style/presets';
import { StyleEditor } from '../style/StyleEditor';
import type { QrStyle, StyleIssue, StylePreset } from '../style/types';
import { ART_THEMES, isHttpsUrl, qrHref } from './utils';
import { describeApiError, errorCode } from './errors';
import { itemsOf, useApiQuery } from './hooks';
import type { CreateQrBody, CreateQrResult, QrCreateType, QrDetail } from './types';
import { useOrgApi } from './useOrgApi';


const STEPS = ['Datos', 'Tipo', 'Estilo', 'Revisar'] as const;
const PII_HINT = /(\b\d{1,2}\.?\d{3}\.?\d{3}-?[\dkK]\b)|(@)|(\+?56\s?9\s?\d{4}\s?\d{4})/;

const TYPES: { id: QrCreateType; title: string; text: string }[] = [
  { id: 'classic', title: 'Clásico', text: 'Negro sobre blanco. Listo al instante y máxima lectura.' },
  { id: 'styled', title: 'Con estilo', text: 'Elige puntos, esquinas, colores, el logo del negocio al centro y un marco.' },
  { id: 'art', title: 'QR Art', text: 'Imagen generada con el QR integrado. Puede tardar hasta 80 s si no hay una lista.' },
];

const DEGRADATION: Record<string, string> = {
  eyes_squared: 'Tu estilo no escaneaba bien: ajustamos las esquinas a cuadradas y mantuvimos tus colores y logo.',
  classic_same_colors: 'Tu estilo no escaneaba bien ni con esquinas cuadradas: lo entregamos como Clásico con tus colores para asegurar la lectura.',
  classic_black: 'Tu estilo no escaneaba bien: lo entregamos como Clásico en negro sobre blanco para asegurar la lectura.',
};

export function CreateQrWizard({ pollMs = 5000 }: { pollMs?: number }) {
  const client = useOrgApi();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [touched, setTouched] = useState(false);
  const [type, setType] = useState<QrCreateType>('styled');
  const [style, setStyle] = useState<QrStyle>(LOCAL_PRESETS.find((p) => p.id === 'marca')!.qrStyle);
  const [issues, setIssues] = useState<StyleIssue[]>([]);
  const [theme, setTheme] = useState('cordillera');
  const [prompt, setPrompt] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<StyleIssue[]>([]);
  const [result, setResult] = useState<CreateQrResult | null>(null);
  const [idemKey, setIdemKey] = useState(newIdempotencyKey);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const presets = useApiQuery(
    (signal) => client.get<{ items: StylePreset[] }>('/v1/qr-styles/presets', { signal }).then(itemsOf).catch(() => [] as StylePreset[]),
    [client],
  );

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    headingRef.current?.focus();
  }, [step, result]);

  const urlError = targetUrl.trim() && !isHttpsUrl(targetUrl) ? 'La dirección no es válida. Usa una URL completa que empiece con https://.' : undefined;
  const dataValid = name.trim().length > 0 && isHttpsUrl(targetUrl) && externalRef.length <= 128;
  const piiWarning = PII_HINT.test(name) || PII_HINT.test(externalRef);
  const styleValid = type !== 'styled' || issues.length === 0;
  const canNext = step === 0 ? dataValid : step === 2 ? styleValid && (type !== 'art' || Boolean(theme || prompt.trim())) : true;

  function next() {
    setTouched(true);
    if (!canNext) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function pollArt(code: string, domain: string) {
    timer.current = setTimeout(async () => {
      try {
        const detail = await client.get<QrDetail>(`/v1/qrs/${encodeURIComponent(code)}${domain !== 'q-r.cl' ? `?domain=${encodeURIComponent(domain)}` : ''}`);
        setResult((prev) => ({ ...(prev as CreateQrResult), ...detail }));
        if (detail.artStatus === 'generando') pollArt(code, domain);
      } catch {
        pollArt(code, domain);
      }
    }, pollMs);
  }

  async function create() {
    setError(null);
    setServerIssues([]);
    setCreating(true);
    const body: CreateQrBody = {
      type,
      business: { name: name.trim(), externalRef: externalRef.trim() || undefined },
      targetUrl: targetUrl.trim(),
      qrStyle: type === 'styled' ? stripLogoUrl(style) : undefined,
      art: type === 'art' ? { theme: prompt.trim() ? undefined : theme, prompt: prompt.trim() || undefined } : undefined,
    };
    try {
      const res = await client.post<CreateQrResult>('/v1/qrs', body, { idempotencyKey: idemKey });
      setResult(res);
      if (res.artStatus === 'generando') pollArt(res.code, res.domain ?? 'q-r.cl');
    } catch (err) {
      const code = errorCode(err);
      const details = err instanceof ApiError ? (err.details as { issues?: StyleIssue[] } | undefined) : undefined;
      if (code === 'style_rejected' || code === 'invalid_style') {
        setServerIssues(details?.issues ?? []);
        setStep(2);
      } else if (code === 'destination_invalid' || code === 'destination_flagged' || code === 'pii_field_not_allowed') {
        setStep(0);
      }
      setError(describeApiError(err, 'No pudimos crear el QR.'));
      // Un 4xx no dejó nada creado: el próximo intento usa una clave nueva.
      if (err instanceof ApiError && err.status < 500) setIdemKey(newIdempotencyKey());
    } finally {
      setCreating(false);
    }
  }

  if (result) {
    return <WizardResult result={result} headingRef={headingRef} onAnother={() => navigate(0)} />;
  }

  return (
    <section className="space-y-5">
      <Link to="/consola/qr" className="text-sm text-green underline hover:text-ink">← QR activados</Link>
      <div>
        <h1 ref={headingRef} tabIndex={-1} className="font-display text-xl font-bold text-ink focus:outline-none">
          Crear QR · {STEPS[step]}
        </h1>
        <p className="text-sm text-ink-muted">El QR queda activado de inmediato; no pasa por lote ni custodia.</p>
      </div>
      <ol className="flex flex-wrap gap-2 text-xs" aria-label="Pasos">
        {STEPS.map((label, i) => (
          <li key={label} aria-current={i === step ? 'step' : undefined} className={`rounded-md px-2 py-1 ${i === step ? 'bg-action font-semibold text-ink' : i < step ? 'bg-gray-100 text-gray-700' : 'bg-gray-200 text-gray-600'}`}>
            {i < step ? '✓ ' : `${i + 1}. `}
            {label}
          </li>
        ))}
      </ol>

      <div className="rounded-lg border border-line bg-white p-5">
        {step === 0 && (
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Nombre del negocio" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} error={touched && !name.trim() ? 'Indica el nombre del negocio.' : undefined} />
            <Input label="Referencia en tu sistema (opcional)" mono value={externalRef} maxLength={128} onChange={(e) => setExternalRef(e.target.value)} placeholder="rest_9120" />
            <div className="md:col-span-2">
              <Input label="Destino" type="url" inputMode="url" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} placeholder="https://" error={urlError ?? (touched && !targetUrl.trim() ? 'Indica a dónde lleva el QR.' : undefined)} />
            </div>
            {piiWarning && (
              <div className="md:col-span-2">
                <Banner tone="warning">No guardamos RUT, email ni teléfono del negocio. Si los escribes, se filtran antes de guardar.</Banner>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <fieldset>
            <legend className="sr-only">Tipo de QR</legend>
            <div className="grid gap-3 md:grid-cols-3">
              {TYPES.map((t) => (
                <label key={t.id} className={`flex cursor-pointer flex-col gap-2 rounded-lg border p-4 focus-within:outline focus-within:outline-2 focus-within:outline-green ${type === t.id ? 'border-asphalt bg-yellow-soft' : 'border-line hover:bg-mist'}`}>
                  <span className="flex items-center gap-2">
                    <input type="radio" name="qr-type" value={t.id} checked={type === t.id} onChange={() => setType(t.id)} />
                    <b>{t.title}</b>
                  </span>
                  <span className="text-xs text-gray-700">{t.text}</span>
                </label>
              ))}
            </div>
            <p className="mt-3 text-xs text-ink-muted">Partner: sin cuota de QR Art.</p>
          </fieldset>
        )}

        {step === 2 && type === 'classic' && <p className="text-sm text-gray-700">El QR Clásico no lleva estilo: negro sobre blanco con máxima lectura. Sigue para revisar.</p>}
        {step === 2 && type === 'styled' && (
          <div className="space-y-3">
            {serverIssues.length > 0 && (
              <Banner tone="warning">{serverIssues.map((i) => i.message).join(' ')}</Banner>
            )}
            <StyleEditor value={style} onChange={setStyle} orgId={client.orgId} presets={presets.data} onIssues={setIssues} />
          </div>
        )}
        {step === 2 && type === 'art' && (
          <div className="space-y-3">
            <fieldset>
              <legend className="mb-2 text-xs font-medium text-ink-muted">Elige una temática o describe la imagen</legend>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {ART_THEMES.map((t) => (
                  <label key={t.id} className={`flex cursor-pointer flex-col gap-1 rounded-lg border p-2 text-xs focus-within:outline focus-within:outline-2 focus-within:outline-green ${theme === t.id && !prompt.trim() ? 'border-asphalt bg-yellow-soft font-semibold' : 'border-line'}`}>
                    <input type="radio" name="art-theme" className="sr-only" checked={theme === t.id} onChange={() => setTheme(t.id)} />
                    <span className="h-16 rounded bg-[radial-gradient(circle,#2F6B57_1.5px,transparent_1.5px)] bg-[length:8px_8px]" aria-hidden="true" />
                    {t.label}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="flex flex-col gap-1">
              <label htmlFor="art-prompt" className="text-xs font-medium text-ink-muted">O describe la imagen (máx. 300)</label>
              <textarea id="art-prompt" maxLength={300} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="h-16 w-full rounded-sm border border-line px-3 py-2 text-sm" placeholder="Taza de café humeante con los cerros de Valparaíso al atardecer" />
            </div>
          </div>
        )}

        {step === 3 && (
          <dl className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-xs text-ink-muted">Negocio</dt>
            <dd>{name}</dd>
            <dt className="text-xs text-ink-muted">Referencia</dt>
            <dd className="font-mono">{externalRef || '—'}</dd>
            <dt className="text-xs text-ink-muted">Destino</dt>
            <dd className="break-all">{targetUrl}</dd>
            <dt className="text-xs text-ink-muted">Tipo</dt>
            <dd>{TYPES.find((t) => t.id === type)?.title}</dd>
            {type === 'art' && (
              <>
                <dt className="text-xs text-ink-muted">Imagen</dt>
                <dd>{prompt.trim() || ART_THEMES.find((t) => t.id === theme)?.label}</dd>
              </>
            )}
            <dd className="col-span-2 mt-2 text-xs text-ink-muted">
              {type === 'classic' && 'Se crea al instante.'}
              {type === 'styled' && 'Verificamos la lectura con varios lectores y simulación de celular antes de entregarlo. Tarda unos segundos.'}
              {type === 'art' && 'Si hay una imagen lista de esta temática sale en menos de un segundo; si no, se genera en 60 a 80 segundos y puedes cerrar esta pantalla.'}
            </dd>
          </dl>
        )}
      </div>

      {error && <Banner tone="error">{error}</Banner>}

      <div className="flex justify-between gap-2">
        <Button variant="secondary" onClick={() => (step === 0 ? navigate('/consola/qr') : setStep(step - 1))}>
          {step === 0 ? 'Cancelar' : 'Atrás'}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} disabled={step !== 0 && !canNext}>Continuar</Button>
        ) : (
          <Button onClick={() => void create()} loading={creating}>
            {type === 'art' ? 'Crear QR Art' : 'Crear QR'}
          </Button>
        )}
      </div>
    </section>
  );
}

function WizardResult({ result, headingRef, onAnother }: { result: CreateQrResult; headingRef: React.RefObject<HTMLHeadingElement>; onAnother: () => void }) {
  const generating = result.artStatus === 'generando';
  const artFailed = result.artStatus === 'fallido';
  const reason = result.styleNotice?.reason ?? (result.styleResult?.degraded ? result.styleResult.reason : null);
  const degradedText = reason ? (DEGRADATION[reason] ?? result.styleNotice?.message ?? 'Ajustamos el estilo para que escanee.') : null;
  const image = result.imageUrl ?? result.image ?? null;
  const target = result.targetUrl?.replace(/^https?:\/\//, '');

  return (
    <section className="space-y-5">
      <div aria-live="polite" className="space-y-4 rounded-lg border border-line bg-white p-5">
        {generating ? (
          <>
            <h1 ref={headingRef} tabIndex={-1} className="font-display text-xl font-bold text-ink focus:outline-none">Generando tu QR Art…</h1>
            <div className="h-2 overflow-hidden rounded-full bg-gray-200">
              <div className="h-2 w-1/2 animate-pulse rounded-full bg-green motion-reduce:animate-none" />
            </div>
            <p className="text-sm text-gray-700">
              Tarda entre 60 y 80 segundos. Puedes salir de esta pantalla: el QR <span className="font-mono">{result.code}</span> ya existe y muestra la página del partner hasta tener imagen.
            </p>
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex h-40 w-40 items-center justify-center border border-line bg-white p-2">
              {image ? <img src={image} alt={`QR ${result.code}`} className="h-full w-full object-contain" /> : <div className="h-full w-full bg-[repeating-conic-gradient(#16181D_0_25%,#fff_0_50%)] bg-[length:14px_14px]" aria-hidden="true" />}
            </div>
            <div className="space-y-2">
              <h1 ref={headingRef} tabIndex={-1} className="font-display text-xl font-bold text-ink focus:outline-none">
                QR <span className="font-mono">{result.code}</span> activado{result.qrStyle && !artFailed ? ' con estilo' : ''}
              </h1>
              <p className="text-sm text-gray-700">{target ? `Ya lleva a ${target}.` : 'Ya está activo.'}{result.styleResult || result.scannable ? ' Lectura verificada.' : ''}</p>
              <div className="flex flex-wrap gap-2">
                {image && (
                  <a href={image} download={`${result.code}.png`} target="_blank" rel="noopener" className="inline-flex items-center rounded-md bg-action px-4 py-2 text-sm font-semibold text-ink no-underline hover:bg-action-hover">
                    Descargar PNG
                  </a>
                )}
                <Link to={qrHref(result)} className="inline-flex items-center rounded-md border border-line bg-white px-4 py-2 text-sm font-semibold text-ink no-underline hover:bg-mist">
                  Ver ficha
                </Link>
              </div>
            </div>
          </div>
        )}
        {degradedText && <Banner tone="warning">{degradedText}</Banner>}
        {artFailed && (
          <Banner tone="warning">
            La imagen no pasó la verificación de lectura. El QR <span className="font-mono">{result.code}</span> ya está activo y lleva al destino; desde la ficha puedes usarlo con estilo o intentar otra temática.
          </Banner>
        )}
        {result.piiNotice?.message && <Banner tone="warning">{result.piiNotice.message}</Banner>}
      </div>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onAnother}>Crear otro QR</Button>
        <Link to="/consola/qr" className="inline-flex items-center px-2 text-sm font-medium text-green underline hover:text-ink">Ver QR activados</Link>
      </div>
    </section>
  );
}
